import { invalidPhoneNumber } from "@notemeal/utils-phone-number";
import { subYears } from "date-fns";
import { z } from "zod";
export const IntegerSchema = z.coerce
    .string()
    .transform(n => (Number.isNaN(parseInt(n, 10)) ? null : parseInt(n, 10)))
    .refine((n) => n !== null, "Required");
export const FloatSchema = z.coerce
    .string()
    .transform(n => (Number.isNaN(parseFloat(n)) ? null : parseFloat(n)))
    .refine((n) => n !== null, "Required");
export const OptionalFloatSchema = z.coerce
    .string()
    .transform(n => (Number.isNaN(parseFloat(n)) ? null : parseFloat(n)));
export const EmailSchema = z.string().email("Enter a valid email address (e.g., name@example.com).");
export const PhoneNumberSchmea = z.string().refine(val => !invalidPhoneNumber(val), { message: "Phone number must contain 10 digits." });
export const DateSchema = z.coerce.date({
    // zod v4 dropped the dedicated "invalid_date" code -- a failed date coercion now reports
    // "invalid_type" (expected: "date") like any other type mismatch.
    // temporary custom error for date workaround from: https://github.com/colinhacks/zod/issues/1526
    error: issue => (issue.code === "invalid_type" && issue.expected === "date" ? "Enter a valid date (e.g., 01/15/2005)." : undefined),
});
export const BirthDateSchema = DateSchema.max(subYears(new Date(), 13), "Athlete must be at least 13");
export const NamedTagsSchema = z.array(z.object({
    tagName: z.object({ name: z.string(), color: z.string() }),
    tag: z.object({ __typename: z.any(), id: z.string() }),
}));
export const SexTypeSchema = z.enum(["male", "female", "%future added value"]);
export const MealTypeSchema = z.enum(["breakfast", "lunch", "dinner", "meal", "snack", "%future added value"]);
export const FormFieldsSchema = z.enum(["height", "weight", "percentBodyFat", "%future added value"]);
export const RMRMethodSchema = z.enum(["average", "cunningham", "harrisBenedict", "mifflin", "%future added value"]);
export const ActivityTypeSchema = z.enum(["class", "competition", "conditioning", "lift", "practice", "%future added value"]);
export const DayOfWeekSchema = z.enum(["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"]);
export const JerseyNumberSchema = z
    .string()
    .regex(/^[A-Za-z0-9]*$/, "No symbols")
    .max(3, "3 characters max");
export const PositionSchema = z.object({ id: z.string(), name: z.string(), __typename: z.literal("Position") });
export const round = (n, places = 1) => Math.round(10 ** places * n) / 10 ** places;
export const heightToInches = (height) => {
    const [feetToken, inchesToken] = height.split("'");
    return 12 * parseInt(feetToken) + round(parseFloat(inchesToken), 1);
};
export const HeightSchema = z
    .string({ error: "Required" })
    .refine(value => (value ? Boolean(heightToInches(value)) : true), { message: "Invalid" });
export const PercentBodyFatSchema = FloatSchema.refine(value => value >= 0 && value <= 100, { message: "Invalid" });
export const OptionalPercentBodyFatSchema = OptionalFloatSchema.refine(value => value === null || (value >= 0 && value <= 100), { message: "Invalid" }).nullable();
export const AnthropometryEntryTypeSchema = z.enum(["estimate", "dexa", "bodpod", "bioelectricalImpedance", "isak", "weighIn", "ams", "%future added value"], {
    error: "Required",
});
export const BoneMineralDensityZScoreSchema = FloatSchema.refine(value => value <= 5 && value >= -5, { message: "Invalid" });
export const invalidVFAValue = (value) => {
    if (!value) {
        return false;
    }
    return value === null || value < 0;
};
export const VisceralFatAreaSchema = FloatSchema.refine(value => !invalidVFAValue(value), { message: "Invalid" });
const invalidMassValue = (value, weight) => {
    if (!value || !weight) {
        return false;
    }
    return value === null || value > weight || value < 0;
};
export const applyInvalidMassValueError = (value, weight, ctx, path) => {
    if (invalidMassValue(value, weight)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Invalid",
            path: [path],
        });
    }
};
export const GoalTypeSchema = z.object({
    __typename: z.literal("GoalType"),
    id: z.string(),
    name: z.string(),
    defaultKcalOffset: z.number(),
});
const BaseAthleteSchema = z.object({
    firstName: z.string({ error: "First Name is required." }).min(1, "First Name is required."),
    lastName: z.string({ error: "Last Name is required." }).min(1, "Last Name is required."),
    sex: SexTypeSchema.nullable(),
    birthDate: BirthDateSchema.nullable(),
    phoneNumber: PhoneNumberSchmea.or(z.literal("")),
    position: PositionSchema.nullable(),
    inbodyUid: z.string().nullable(),
    secaUid: z.string().nullable(),
    jerseyNumber: JerseyNumberSchema.nullable(),
    teamId: z.string(),
});
export const RequireEmailAthleteSchema = BaseAthleteSchema.extend({
    email: z.string({ error: "Required" }).min(1, "Required").email("Enter a valid email address (e.g., name@example.com)."),
});
export const AthleteSchema = BaseAthleteSchema.extend({
    email: EmailSchema.or(z.literal("")),
});
//# sourceMappingURL=validators.js.map